import { oc } from "@orpc/contract";
import { z } from "zod";
import { GetDemandMatrixInputSchema } from "./forecast-runs.contract";
import { GetForecastDetailInputSchema } from "./forecast-detail.contract";
import { StartFileExportResponseSchema } from "../core/file-exports.contract";

/**
 * Excel exports of the buyer-facing procurement screens (GEN-2683).
 *
 * Each export takes the same filters its screen sends, so the file is the
 * screen's current view — but the whole result set, not the page on screen.
 * Each procedure starts a background file export and returns its job id; see
 * `core/file-exports.contract.ts` for how the browser collects the file. The
 * workbook carries an "Export info" sheet with the timestamp, user and filters
 * so it stands on its own offline.
 */

/** The demand grid's request, minus paging — the export takes every page. */
export const ExportDemandMatrixInputSchema = GetDemandMatrixInputSchema.omit({
  page: true,
  pageSize: true,
});
export type ExportDemandMatrixInput = z.infer<
  typeof ExportDemandMatrixInputSchema
>;
/** Pre-parse shape for callers, where the defaulted fields are optional. */
export type ExportDemandMatrixArgs = z.input<
  typeof ExportDemandMatrixInputSchema
>;

export const ExportForecastDetailInputSchema = GetForecastDetailInputSchema;
export type ExportForecastDetailInput = z.infer<
  typeof ExportForecastDetailInputSchema
>;

/** The board's buy-vs-transfer toggle: every line, buys only, transfers only. */
export const ReplenishmentExportViewSchema = z.enum(["all", "po", "to"]);

export const ExportReplenishmentSuggestionsInputSchema = z.object({
  divisionIds: z.array(z.string().uuid()).min(1),
  /** Destination warehouses; omitted means every warehouse in the divisions. */
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  /** The board's supplier search: supplier (or transfer source) name contains. */
  search: z.string().optional(),
  view: ReplenishmentExportViewSchema.optional().default("all"),
  /** Keep only suppliers with at least one sales-order-driven line. */
  soOnly: z.boolean().optional(),
});
export type ExportReplenishmentSuggestionsInput = z.infer<
  typeof ExportReplenishmentSuggestionsInputSchema
>;
export type ExportReplenishmentSuggestionsArgs = z.input<
  typeof ExportReplenishmentSuggestionsInputSchema
>;

export const procurementExportsContracts = {
  demandMatrix: oc
    .input(ExportDemandMatrixInputSchema)
    .output(StartFileExportResponseSchema)
    .route({
      method: "POST",
      summary: "Export the demand forecast grid to Excel",
      description:
        "Starts a background Excel export of every forecast summary row matching the filters; returns a job id for downloading the file.",
    }),
  forecastDetail: oc
    .input(ExportForecastDetailInputSchema)
    .output(StartFileExportResponseSchema)
    .route({
      method: "POST",
      summary: "Export a SKU's forecast detail to Excel",
      description:
        "Starts a background Excel export of one SKU's forecast detail across the filtered locations; returns a job id for downloading the file.",
    }),
  replenishmentSuggestions: oc
    .input(ExportReplenishmentSuggestionsInputSchema)
    .output(StartFileExportResponseSchema)
    .route({
      method: "POST",
      summary: "Export replenishment suggestions to Excel",
      description:
        "Starts a background Excel export of replenishment suggestion lines for the chosen divisions, warehouses, supplier search and buy/transfer view; returns a job id.",
    }),
} as const;
